import { useCallback, useEffect, useRef, useState } from 'react';

import { getAccessToken } from '../../../auth';
import { dispatchSessionExpiredIfUnauthorized, isSessionExpired } from '../../../auth/sessionExpiry';
import config from '../../../../assets/config';
import {
  buildDiagnosticsResultProperties,
  buildFailedCheckToRunProperties,
  createDiagnosticsRequestId,
  type DiagnosticsFailureReason,
  type DiagnosticsRunTrigger,
  getDiagnosticsStepName,
} from '../DiagnosticsAnalyticsEvents';
import type { DiagnosticsReportResponse } from '../api/diagnosticsReportApi';
import { EventSourcePost } from '../EventSourcePost';
import { DiagnosticsReportEvent, DiagnosticsState, INITIAL_DIAGNOSTICS_STATE } from '../DiagnosticsTypes';
import { VISIBLE_STEPS } from '../diagnosticsStepMap';
import useTrackDiagnosticsEvent from './useTrackDiagnosticsEvent';
import { useIsAccountExpired } from '../../../../hooks/useIsAccountExpired';

type DiagnosticsRunState = {
  startedAt: number | null;
  requestId: string;
  url: string;
  attempts: number;
  completedVisibleSteps: Set<string>;
  hasTerminalEvent: boolean;
  crawlers?: string[];
};

const INITIAL_RUN_STATE: DiagnosticsRunState = {
  startedAt: null,
  requestId: '',
  url: '',
  attempts: 1,
  completedVisibleSteps: new Set(),
  hasTerminalEvent: false,
};

export function useDiagnostics() {
  const [diagnosticsState, setDiagnosticsState] = useState<DiagnosticsState>(INITIAL_DIAGNOSTICS_STATE);
  const diagnosticsSourceRef = useRef<EventSourcePost | null>(null);
  const runStateRef = useRef<DiagnosticsRunState>({ ...INITIAL_RUN_STATE });
  const trackDiagnosticsEvent = useTrackDiagnosticsEvent();
  const isAccountExpired = useIsAccountExpired();

  useEffect(() => {
    return () => {
      diagnosticsSourceRef.current?.close();
    };
  }, []);

  const getElapsedMs = useCallback(() => {
    if (!runStateRef.current.startedAt) {
      return 0;
    }

    return Date.now() - runStateRef.current.startedAt;
  }, []);

  const trackDiagnosticsResult = useCallback(
    ({
      domain,
      failureReason,
      requestId,
      reportId,
      url,
      userAgents,
    }: {
      domain: string;
      failureReason: '' | DiagnosticsFailureReason;
      requestId: string;
      reportId: string;
      url: string;
      userAgents?: DiagnosticsReportResponse['userAgents'];
    }) => {
      trackDiagnosticsEvent(
        'Diagnostics: Result',
        buildDiagnosticsResultProperties({
          attempts: runStateRef.current.attempts,
          domain,
          duration_ms: getElapsedMs(),
          failure_reason: failureReason,
          request_id: requestId,
          report_id: reportId,
          url,
          userAgents,
        })
      );
    },
    [getElapsedMs, trackDiagnosticsEvent]
  );

  const runDiagnostics = useCallback(
    async (domain: string, options: { runTrigger: DiagnosticsRunTrigger; requestId?: string; crawlers?: string[] }) => {
      // Backstop for the per-trigger disabled buttons: an expired account must
      // never start a run, even from a trigger this guard's callers missed.
      if (isAccountExpired) {
        return;
      }

      // Once the session is expired the unified modal is already showing; fail fast
      // instead of opening an SSE request that is guaranteed to 401.
      if (isSessionExpired()) {
        return;
      }

      diagnosticsSourceRef.current?.close();
      // A rerun from the error state retries the same run, so it keeps the
      // crawler scope of the run it retries instead of reading `options`.
      const crawlers =
        options.runTrigger === 'progress_modal_error_state' ? runStateRef.current.crawlers : options.crawlers;
      runStateRef.current = {
        ...INITIAL_RUN_STATE,
        requestId: options.requestId ?? createDiagnosticsRequestId(),
        startedAt: Date.now(),
        completedVisibleSteps: new Set(),
        attempts: options.runTrigger === 'progress_modal_error_state' ? runStateRef.current.attempts + 1 : 1,
        crawlers,
      };
      setDiagnosticsState({
        ...INITIAL_DIAGNOSTICS_STATE,
        loading: true,
        hasDiagnosticsRun: true,
        domain,
        crawlers,
      });

      const diagnosticsSource = new EventSourcePost(`${config.apiUrl.restServer}/v3/ai-insights/diagnostics`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(crawlers?.length ? { domain, crawlers } : { domain }),
      });

      diagnosticsSource.onmessage = ({ data }) => {
        try {
          const event = JSON.parse(data) as DiagnosticsReportEvent;

          if (event.type === 'progress') {
            runStateRef.current.url = event.url;
            VISIBLE_STEPS.forEach((step, stepIndex) => {
              if (event.checks[step.key] !== true || runStateRef.current.completedVisibleSteps.has(step.key)) {
                return;
              }

              runStateRef.current.completedVisibleSteps.add(step.key);
              trackDiagnosticsEvent('Diagnostics: Progress Step', {
                request_id: runStateRef.current.requestId,
                domain,
                url: event.url,
                step_index: stepIndex,
                step_name: getDiagnosticsStepName(step.key),
                step_status: 'passed',
                elapsed_ms: getElapsedMs(),
              });
            });

            setDiagnosticsState((currentState) => ({
              ...currentState,
              diagnosticsChecks: { ...currentState.diagnosticsChecks, ...event.checks },
              diagnosticsError: undefined,
            }));
          }

          if (event.type === 'complete') {
            runStateRef.current.hasTerminalEvent = true;
            runStateRef.current.url = event.url;
            const reportId = event.reportId ?? '';

            trackDiagnosticsResult({
              domain,
              failureReason: '',
              requestId: runStateRef.current.requestId,
              reportId,
              url: event.url,
              userAgents: event.userAgents,
            });

            for (const failedCheck of buildFailedCheckToRunProperties({
              request_id: runStateRef.current.requestId,
              userAgents: event.userAgents,
            })) {
              trackDiagnosticsEvent('Diagnostics: Check Failed To Run', failedCheck);
            }

            setDiagnosticsState((currentState) => ({
              ...currentState,
              diagnosticsError: undefined,
              reportId: reportId || undefined,
            }));
          }

          if (event.type === 'error') {
            runStateRef.current.hasTerminalEvent = true;
            runStateRef.current.url = event.url;
            const message = event.url ? event.message : `${event.message} (no URL available?)`;

            trackDiagnosticsResult({
              domain,
              failureReason: 'internal_error',
              requestId: runStateRef.current.requestId,
              reportId: '',
              url: event.url,
            });

            setDiagnosticsState((currentState) => ({
              ...currentState,
              diagnosticsError: message,
            }));
          }
        } catch (error) {
          setDiagnosticsState((currentState) => ({
            ...currentState,
            diagnosticsError: 'Failed to parse diagnostics event',
          }));
        }
      };

      diagnosticsSource.onerror = (error) => {
        if (dispatchSessionExpiredIfUnauthorized((error as { status?: number } | null)?.status)) {
          // Session expiry is handled globally by SessionExpiredModal — don't log
          // it as a connection failure, overwrite the UI with the generic
          // connection-lost copy, or record it as a connection drop.
          return;
        }

        console.error(error);

        if (!runStateRef.current.hasTerminalEvent) {
          runStateRef.current.hasTerminalEvent = true;
          trackDiagnosticsResult({
            domain,
            failureReason: 'connection_drop',
            requestId: runStateRef.current.requestId,
            reportId: '',
            url: runStateRef.current.url,
          });
        }

        setDiagnosticsState((currentState) => ({
          ...currentState,
          diagnosticsError: 'Connection lost. Please try again.',
        }));
      };

      diagnosticsSource.onclose = () => {
        if (diagnosticsSourceRef.current === diagnosticsSource) {
          diagnosticsSourceRef.current = null;
          setDiagnosticsState((currentState) => ({
            ...currentState,
            loading: false,
          }));
        }
      };

      diagnosticsSourceRef.current = diagnosticsSource;
    },
    [getElapsedMs, isAccountExpired, trackDiagnosticsEvent, trackDiagnosticsResult]
  );

  const dismissDiagnostics = useCallback(() => {
    setDiagnosticsState(INITIAL_DIAGNOSTICS_STATE);
  }, []);

  return { diagnosticsState, runDiagnostics, dismissDiagnostics };
}
